import * as React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { Button, Toast } from 'antd-mobile'
import './index.scss'
import {
  getFaqDetail,
  getFAQRecommend,
  getFAQCountHelp,
  setFaqStar,
  setFaqUnStar
} from '@feedback/services/index'
import SearchInput from '@feedback/components/SearchInput'
import htmlToReact from '@feedback/pages/allfb/questionDetail/components/htmlToReact'
import { getTextByKey, reportFn, tracking } from '@feedback/utils/util'
import { queryClient } from '@feedback/pages/allfb/App'
import { getAppConfig } from '@feedback/utils/CONST'
import { isVestAppid } from '@feedback/utils/siteConfig'
import VestLayout from '@feedback/pages/allfb/vest/Layout'
import VestDissatisfactionSheet, {
  DISSATISFACTION_ENABLED
} from '@feedback/components/VestDissatisfactionSheet'
import clsx from 'clsx'

const thumbsUpUnselected: string = require('@feedback/assets/vest/icons/thumbs-up-unselected.svg')
const thumbsDownUnselected: string = require('@feedback/assets/vest/icons/thumbs-down-unselected.svg')
const contactIcon: string = require('@feedback/assets/vest/icons/contact.svg')

// 详情
export interface IQuestion {
  _id: string
  answer: string
  app_id: string
  created_at: string
  is_deleted: number
  is_sync: number
  language_id: string
  question: string
  star: number
  type_id: number
  unstar: number
  weight: number
  faq_type: string
  qus_id: number
}

export const getQuestionDetail = async (faq_id: string): Promise<IQuestion> => {
  const { code, data, message } = (await getFaqDetail({ faq_id })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  return data
}

// 推荐
export interface IQuestionRecommend {
  _id: string
  question: string
}

export const getQuestionRecommend = async (
  faq_id?: string
): Promise<IQuestionRecommend[]> => {
  const { code, data, message } = (await getFAQRecommend({ faq_id })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  return data
}

// 点赞统计
export const getQuestionCountHelp = async (
  faq_id: string
): Promise<{
  total_count: number
  star_count: number
}> => {
  const { code, data, message } = (await getFAQCountHelp({ faq_id })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

// 点赞
export const setStar = async (faq_id: string) => {
  const { code, data, message } = (await setFaqStar({ faq_id })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

// 点踩（马甲站可携带不满意反馈原因 comment）
export const setUnStar = async ({
  faq_id,
  comment
}: {
  faq_id: string
  comment?: string
}) => {
  const { code, data, message } = (await setFaqUnStar({ faq_id, comment })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

export const BackTop: React.FC<{
  parentOffsetHeight?: number
  parentOffsetTop?: number
}> = ({ parentOffsetHeight, parentOffsetTop }) => {
  const [showBackTop, setShowBackTop] = React.useState(false)
  const { appid } = getPageConfig()

  React.useEffect(() => {
    if (parentOffsetHeight) {
      setShowBackTop(document.body.scrollHeight > window.innerHeight)
    }
  }, [parentOffsetHeight])

  if (!showBackTop) return null

  return (
    <div
      className={clsx('faq-detail-backtop')}
      onClick={() => {
        window.scrollTo(0, Number(parentOffsetTop))
      }}
    >
      <i className='faq-detail-backtop__icon' />
      {getTextByKey('Return to top')}
    </div>
  )
}

const getPageConfig = () => {
  const { appid, logo, logoShadow, bgSearch } = getAppConfig() || {}
  return {
    ...(appid === 'cm1001' && { logo }),
    ...(appid === 'cm1002' && { logo }),
    ...(appid === 'cm1003' && { logo }),
    ...(appid === 'cm1006' && { logo }),
    ...(appid === 'cm1007' && { logo }),
    ...(appid === 'cm1008' && { logo: logoShadow }),
    ...(appid === 'cm1009' && { logo }),
    ...(appid === 'cm1017' && { logo }),
    ...(appid === 'cm1011' && { logo: logoShadow }),
    ...(appid === 'cm1012' && { logo }),
    ...(appid === 'cm1013' && { logo }),
    ...(appid === 'cm1018' && { logo }),
    ...(appid === 'cm1015' && { logo }),
    ...(appid === 'cm3002' && { logo }),
    appid,
    bgSearch
  }
}

const QuestionDetail: React.FC = () => {
  const { faq_id } = Object.fromEntries(
    new URLSearchParams(window.location.href.split('?')[1])
  )
  const questionDetail = useQuery<IQuestion, Error>(
    ['getQuestionDetail', faq_id],
    () => getQuestionDetail(faq_id),
    {
      enabled: !!faq_id
    }
  )
  const questionRecommend = useQuery<IQuestionRecommend[], Error>(
    ['getQuestionRecommend', faq_id],
    () => getQuestionRecommend(faq_id),
    {
      enabled: !!faq_id
    }
  )
  const questionCountHelp = useQuery(
    ['getQuestionCountHelp', faq_id],
    () => getQuestionCountHelp(faq_id),
    {
      enabled: !!faq_id
    }
  )
  const questionStar = useMutation(setStar, {
    onSuccess: async () => {
      queryClient.invalidateQueries('getQuestionCountHelp')
      window.localStorage.setItem(`feedback-help-status-${faq_id}`, '1')
    },
    onError: async ({ message }: Error) => {
      Toast.info(`star fail${message ? ` , ${message}` : ''}`)
    }
  })
  const questionUnStar = useMutation(setUnStar, {
    onSuccess: async () => {
      queryClient.invalidateQueries('getQuestionCountHelp')
      window.localStorage.setItem(`feedback-help-status-${faq_id}`, '2')
    },
    onError: async ({ message }: Error) => {
      Toast.info(`unStar fail${message ? ` , ${message}` : ''}`)
    }
  })

  const history = useHistory()
  const targetRef = React.useRef<HTMLDivElement>(null)
  const helpStatus =
    Number(window.localStorage.getItem(`feedback-help-status-${faq_id}`)) || 0
  const { appid, logo, bgSearch } = getPageConfig()
  const isVest = isVestAppid(appid)
  // 马甲站「不满意反馈」底部弹层开关（原型 dissatisfaction-sheet）
  const [dissatisfactionOpen, setDissatisfactionOpen] = React.useState(false)

  const goTicketForm = (fromHelpStatus2 = false) => {
    if (appid === 'cm1006') {
      tracking({
        _step_id: 3,
        _feedback_page_id: '',
        _postion_id: 2,
        _search_words: '',
        _faq_id: faq_id,
        _src_faq_id: ''
      })
    } else {
      reportFn({
        parm1: 3,
        parm3: 2,
        parm5: 'h5_feedback_click_2',
        parm9: faq_id
      })
    }
    if (fromHelpStatus2) {
      history.push(`/ticketForm?qus_id=${questionDetail.data?.qus_id}`)
    } else {
      history.push(
        `/ticketForm?qus_id=${questionDetail.data?.qus_id ?? 0}&faq_id=${
          faq_id ?? 0
        }`
      )
    }
  }

  const reportUnhelpful = () => {
    if (appid === 'cm1006') {
      tracking({
        _step_id: '',
        _feedback_page_id: 3,
        _postion_id: 2,
        _search_words: '',
        _faq_id: faq_id,
        _src_faq_id: ''
      })
    } else {
      reportFn({ parm1: 3, parm2: 2, parm6: faq_id })
    }
  }

  // 非马甲站：点踩即时提交
  const onUnhelpful = () => {
    if (!faq_id) return
    questionUnStar.mutate({ faq_id })
    reportUnhelpful()
  }

  // 马甲站：点踩先弹「不满意反馈」弹层，提交时才带原因落库
  const openDissatisfaction = () => {
    if (!faq_id) return
    reportUnhelpful()
    setDissatisfactionOpen(true)
  }

  const submitDissatisfaction = (reason: string) => {
    if (!faq_id) return
    questionUnStar.mutate({ faq_id, comment: reason })
    setDissatisfactionOpen(false)
    Toast.info(getTextByKey('Thanks for your feedback'), 2)
  }

  const onHelpful = () => {
    if (!faq_id) return
    questionStar.mutate(faq_id)
    // 马甲站：有用即 toast 致谢（原型 article-toast），非 vest 保留内联文案
    if (isVest) {
      Toast.info(getTextByKey('Thanks for your feedback'), 2)
    }

    if (appid === 'cm1006') {
      tracking({
        _step_id: '',
        _feedback_page_id: 3,
        _postion_id: 1,
        _search_words: '',
        _faq_id: faq_id,
        _src_faq_id: ''
      })
    } else {
      reportFn({ parm1: 3, parm2: 1, parm6: faq_id })
    }
  }

  const articleAndAppraise = (
    <>
      {isVest ? (
        questionDetail.data?.question ? (
          <h1 className='vest-faq-detail__title'>
            {questionDetail.data.question}
          </h1>
        ) : null
      ) : (
        <div className='faq-detail-breadcrumb'>
          {questionDetail.data?.faq_type}
          {questionDetail.data?.faq_type && questionDetail.data?.question
            ? ' > '
            : ''}
          {questionDetail.data?.question}
        </div>
      )}

      {questionDetail.data?.answer && (
        <section className='faq-detail-article'>
          {htmlToReact({ html: questionDetail.data?.answer })}
        </section>
      )}

      <section className='faq-detail-appraise'>
        {helpStatus === 0 && (
          <>
            <div className='faq-detail-appraise__text'>
              {isVest ? (
                <>
                  <span className='vest-faq-detail__divider' aria-hidden='true' />
                  <p>{getTextByKey('Was this article helpful')}</p>
                </>
              ) : (
                getTextByKey('Was this article helpful')
              )}
            </div>

            <div className='faq-detail-appraise__submit'>
              {isVest ? (
                <>
                  <button
                    type='button'
                    className='faq-detail-button is-vest-vote'
                    disabled={questionUnStar.isLoading}
                    onClick={
                      DISSATISFACTION_ENABLED
                        ? openDissatisfaction
                        : () => {
                            // 弹层暂隐：走原先即时点踩逻辑，补 toast 与 Yes 对称
                            onUnhelpful()
                            Toast.info(
                              getTextByKey('Thanks for your feedback'),
                              2
                            )
                          }
                    }
                  >
                    <img
                      className='vest-faq-detail__thumb'
                      src={thumbsDownUnselected}
                      alt=''
                      aria-hidden='true'
                    />
                    <span>{getTextByKey('No')}</span>
                  </button>
                  <button
                    type='button'
                    className='faq-detail-button is-vest-vote'
                    disabled={questionStar.isLoading}
                    onClick={onHelpful}
                  >
                    <img
                      className='vest-faq-detail__thumb'
                      src={thumbsUpUnselected}
                      alt=''
                      aria-hidden='true'
                    />
                    <span>{getTextByKey('Yes')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Button
                    className={clsx('faq-detail-button', 'is-light')}
                    loading={questionUnStar.isLoading}
                    disabled={questionUnStar.isLoading}
                    onClick={onUnhelpful}
                  >
                    {getTextByKey('No')}
                  </Button>
                  <Button
                    className={clsx('faq-detail-button')}
                    loading={questionStar.isLoading}
                    disabled={questionStar.isLoading}
                    onClick={onHelpful}
                  >
                    {getTextByKey('Yes')}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
        {/* 马甲站有用/无用致谢改由 toast 承担，此处仅非 vest 保留内联文案 */}
        {helpStatus === 1 && !isVest && (
          <div className='faq-detail-appraise__text'>
            {getTextByKey('Thanks for your feedback')}
          </div>
        )}
        {helpStatus === 2 && !isVest && (
          <>
            <div className='faq-detail-appraise__text'>
              {getTextByKey('Still need help')}
            </div>
            <div className='faq-detail-appraise__contact-us'>
              <Button
                className={clsx('faq-detail-button is-full')}
                onClick={() => goTicketForm(true)}
              >
                {getTextByKey('Contact Us')}
              </Button>
            </div>
          </>
        )}
        {!isVest && (helpStatus === 0 || helpStatus === 1) && (
          <div className='faq-detail-appraise__tips'>
            <div className='faq-detail-gray'>
              {getTextByKey('{min} out of {total} found this helpful')
                .replace('{total}', questionCountHelp.data?.total_count || 0)
                .replace('{min}', questionCountHelp.data?.star_count || 0)}
            </div>
            <div className='faq-detail-gray'>
              {getTextByKey('Still need help')}&nbsp;
              <Link
                className={clsx('faq-detail-link')}
                to={`/ticketForm?qus_id=${
                  questionDetail.data?.qus_id ?? 0
                }&faq_id=${faq_id ?? 0}`}
                onClick={() => {
                  if (appid === 'cm1006') {
                    tracking({
                      _step_id: 3,
                      _feedback_page_id: '',
                      _postion_id: 2,
                      _search_words: '',
                      _faq_id: faq_id,
                      _src_faq_id: ''
                    })
                  } else {
                    reportFn({
                      parm1: 3,
                      parm3: 2,
                      parm5: 'h5_feedback_click_2',
                      parm9: faq_id
                    })
                  }
                }}
              >
                {getTextByKey('Contact Us')}
              </Link>
            </div>
          </div>
        )}
        {/* 马甲站：有用/无用后仍展示统计行（无 Contact Us 链接，底栏 CTA 承担） */}
        {isVest && [0, 1, 2].includes(helpStatus) && (
          <div className='faq-detail-appraise__tips'>
            <div className='faq-detail-gray'>
              {getTextByKey('{min} out of {total} found this helpful')
                .replace('{total}', questionCountHelp.data?.total_count || 0)
                .replace('{min}', questionCountHelp.data?.star_count || 0)}
            </div>
          </div>
        )}
      </section>

      <ul className='faq-detail-recommend'>
        <h2>{getTextByKey('Related results')}</h2>
        {Array.isArray(questionRecommend.data) &&
        questionRecommend.data.length > 0 ? (
          questionRecommend.data?.map(({ question, _id }) => (
            <li
              key={_id}
              onClick={() => {
                if (appid === 'cm1006') {
                  tracking({
                    _step_id: 2,
                    _feedback_page_id: '',
                    _postion_id: 3,
                    _search_words: '',
                    _faq_id: faq_id,
                    _src_faq_id: _id
                  })
                } else {
                  reportFn({
                    parm1: 2,
                    parm2: 3,
                    parm5: 'h5_feedback_click_2',
                    parm7: _id,
                    parm8: faq_id
                  })
                }
                history.push(`/questionDetail?faq_id=${_id}`)
              }}
            >
              <span>{question}</span>
              {isVest ? (
                <i className='vest-faq-detail__chevron' aria-hidden='true' />
              ) : null}
            </li>
          ))
        ) : (
          <div className='faq-detail-gray'>{getTextByKey('No data')}</div>
        )}
      </ul>
    </>
  )

  if (isVest) {
    return (
      <VestLayout
        title={getTextByKey('FAQ Content')}
        showBottomBar={false}
        className='vest-faq-detail-shell'
        bodyClassName='vest-faq-detail-body'
        bottomSlot={
          <button
            type='button'
            className='vest-faq-detail__contact'
            onClick={() => goTicketForm(helpStatus === 2)}
          >
            <img src={contactIcon} alt='' aria-hidden='true' />
            <span>{getTextByKey('Contact Us')}</span>
          </button>
        }
      >
        <div className='vest-faq-detail' ref={targetRef}>
          {articleAndAppraise}
        </div>
        {DISSATISFACTION_ENABLED && (
          <VestDissatisfactionSheet
            open={dissatisfactionOpen}
            submitting={questionUnStar.isLoading}
            onClose={() => setDissatisfactionOpen(false)}
            onSubmit={submitDissatisfaction}
          />
        )}
      </VestLayout>
    )
  }

  return (
    <div className={clsx('faq-detail-container', appid)} ref={targetRef}>
      <header className='faq-detail-header'>
        <div
          className='faq-detail-header__bg-search'
          style={{ backgroundImage: `url(${bgSearch})` }}
        />
        <div className={clsx('faq-detail-header__title')}>
          {getTextByKey('FAQ Content')}
        </div>
        <Link to='/'>
          <div
            className='faq-detail-header__logo'
            style={{ backgroundImage: `url(${logo})` }}
          />
        </Link>
        <div className='faq-detail-header__search'>
          <SearchInput
            onSearch={(keyWord) =>
              history.push(
                `/searchResult?${new URLSearchParams({
                  keyWord
                }).toString()}`
              )
            }
          />
        </div>

        {['cm1006', 'cm1007']?.includes(appid) && (
          <div className='round-b'></div>
        )}
      </header>

      {articleAndAppraise}

      <BackTop
        parentOffsetHeight={targetRef?.current?.offsetHeight}
        parentOffsetTop={targetRef?.current?.offsetTop}
      />
    </div>
  )
}

export default QuestionDetail
